import Header from "../components/header/Header";
import StatisticCard from "../components/statistic/StatisticCard";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Card, Table, Tag, message, DatePicker, Select, Button, InputNumber } from "antd";
import {
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  WalletOutlined,
  BankOutlined,
  DollarCircleOutlined,
  LineChartOutlined,
  PercentageOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { apiRequest } from "../utils/apiClient";
import { Line, Column, Pie } from "@ant-design/plots";
import dayjs from "dayjs";
import { getUser } from "../utils/userData";
import checkPosType from "../utils/posType";
import { CHART_COLORS, INVOICE_TYPE_POS, INVOICE_TYPE_DIGITAL_INVOICING } from "../utils/constants";
import { useTranslation } from "react-i18next";
import { setSelectedBranch } from "../redux/userSlice";
import { useVisibilityAwarePolling } from "../hooks/useVisibilityAwarePolling";
import { useSocket } from "../contexts/SocketContext";

const POLL_INTERVAL_MS = 60_000;
const POLL_INTERVAL_WHEN_SOCKET_CONNECTED_MS = 5 * 60_000; // 5 min fallback

const StatisticPage = () => {
  const { t } = useTranslation("pages");
  const dispatch = useDispatch();
  const userRedux = useSelector((state) => state.user);
  const { socket, connected } = useSocket();
  const [invoices, setInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const selectedBranchId = userRedux?.selectedBranchId || "";
  const username = userRedux?.username ?? "";

  const user = getUser();
  const businessName = user?.businessName || "";
  const posType = checkPosType(businessName);
  const isPharmacy = posType === false;
  const isAdmin = userRedux?.role !== "cashier";

  // ================= FETCH DATA =================
  const fetchStatsData = useCallback(async () => {
    try {
      const invParams = new URLSearchParams({ page: 1, limit: 5000, _t: Date.now() });
      const prodParams = new URLSearchParams({ page: 1, limit: 5000 });
      if (selectedBranchId) {
        invParams.set("branchId", selectedBranchId);
        prodParams.set("branchId", selectedBranchId);
      }
      const [invoiceRes, productRes] = await Promise.all([
        apiRequest(`/api/invoices/get-all?${invParams}`, { method: "GET" }),
        apiRequest(`/api/products/get-all?${prodParams}`, { method: "GET" }),
      ]);

      setInvoices(Array.isArray(invoiceRes) ? invoiceRes : invoiceRes?.data || []);
      setProducts(Array.isArray(productRes) ? productRes : productRes?.products || []);
    } catch (error) {
      console.error("Stats fetch failed:", error);
    }
  }, [selectedBranchId]);

  // Real-time: refetch when invoices change via socket
  useEffect(() => {
    if (!socket) return;
    const onInvoiceEvent = () => fetchStatsData();
    socket.on("invoice:created", onInvoiceEvent);
    socket.on("invoice:updated", onInvoiceEvent);
    return () => {
      socket.off("invoice:created", onInvoiceEvent);
      socket.off("invoice:updated", onInvoiceEvent);
    };
  }, [socket, fetchStatsData]);

  // Polling: faster when socket disconnected, slower when connected (fallback only)
  useVisibilityAwarePolling(
    fetchStatsData,
    connected ? POLL_INTERVAL_WHEN_SOCKET_CONNECTED_MS : POLL_INTERVAL_MS
  );

  // Daily sales ledger date range (default last 31 days) – must be before useEffect that uses it
  const [dailyLedgerDateRange, setDailyLedgerDateRange] = useState(() => [
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);
  const [openingBalanceOverrides, setOpeningBalanceOverrides] = useState({});
  const [editingOpeningDate, setEditingOpeningDate] = useState(null);
  const editingOpeningValueRef = useRef(null);
  const [ledgerBranches, setLedgerBranches] = useState([]);

  // Effective branch for daily ledger: selected branch, or cashier's branch, or single branch (so single-branch owners can edit without selecting)
  // When admin has no branches (ledgerBranches.length === 0), editing is still allowed via user-scoped overrides
  const effectiveBranchIdForLedger = useMemo(() => {
    if (selectedBranchId) return selectedBranchId;
    if (userRedux?.branchId) return userRedux.branchId; // cashier
    if (ledgerBranches.length === 1) return ledgerBranches[0]._id;
    return null;
  }, [selectedBranchId, userRedux?.branchId, ledgerBranches]);

  // Can edit opening balance: when we have a branch, or when admin has no branches (user-scoped overrides)
  const canEditOpeningBalance = useMemo(
    () => !!effectiveBranchIdForLedger || (isAdmin && ledgerBranches.length === 0),
    [effectiveBranchIdForLedger, isAdmin, ledgerBranches.length]
  );

  // Load branches when admin (for daily ledger: single-branch owners can edit without selecting; cashiers use userRedux.branchId)
  useEffect(() => {
    if (!isAdmin) {
      setLedgerBranches([]);
      return;
    }
    let cancelled = false;
    const fetchBranches = async () => {
      try {
        const data = await apiRequest("/api/branches", { method: "GET" });
        if (!cancelled) setLedgerBranches(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setLedgerBranches([]);
      }
    };
    fetchBranches();
    return () => { cancelled = true; };
  }, [isAdmin]);

  // Fetch opening balance overrides for daily ledger (effective branch, or user-scoped when admin has no branches)
  useEffect(() => {
    const hasScope = effectiveBranchIdForLedger || (isAdmin && ledgerBranches.length === 0);
    if (!isPharmacy || !hasScope || !dailyLedgerDateRange?.[0] || !dailyLedgerDateRange?.[1]) {
      setOpeningBalanceOverrides({});
      return;
    }
    const fetchOverrides = async () => {
      try {
        const params = new URLSearchParams({
          startDate: dailyLedgerDateRange[0].format("YYYY-MM-DD"),
          endDate: dailyLedgerDateRange[1].format("YYYY-MM-DD"),
        });
        if (effectiveBranchIdForLedger) params.set("branchId", effectiveBranchIdForLedger);
        const data = await apiRequest(`/api/daily-ledger/overrides?${params}`, { method: "GET" });
        setOpeningBalanceOverrides(typeof data === "object" && !Array.isArray(data) ? data : {});
      } catch (err) {
        console.error("Fetch opening balance overrides failed:", err);
        setOpeningBalanceOverrides({});
      }
    };
    fetchOverrides();
  }, [effectiveBranchIdForLedger, isAdmin, ledgerBranches.length, dailyLedgerDateRange]);

  // ================= OVERALL STATS =================
  const stats = useMemo(() => {
    let totalSales = 0;
    let totalTax = 0;
    let totalDiscount = 0;
    let itemsSold = 0;

    invoices.forEach((inv) => {
      totalSales += inv.totalAmount || 0;
      totalTax += inv.tax || 0;
      totalDiscount += inv.discount || 0;

      inv.cartItems?.forEach((i) => {
        itemsSold += i.quantity || 0;
      });
    });

    return {
      totalSales: totalSales.toFixed(2),
      totalTax: totalTax.toFixed(2),
      totalDiscount: totalDiscount.toFixed(2),
      netSales: (totalSales - totalTax - totalDiscount).toFixed(2),
      totalInvoices: invoices.length,
      itemsSold,
    };
  }, [invoices]);

  // ================= LAST 7 DAYS SALES =================
  const last7DaysSales = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const day = dayjs().subtract(6 - i, "days").format("YYYY-MM-DD");
      const total = invoices
        .filter((inv) => dayjs(inv.invoiceDate).format("YYYY-MM-DD") === day)
        .reduce((s, inv) => s + (inv.totalAmount || 0), 0);

      return { date: day, total: +total.toFixed(2) };
    });
  }, [invoices]);

  // ================= MONTHLY SALES =================
  const monthlySales = useMemo(() => {
    const map = {};
    invoices.forEach((inv) => {
      const month = dayjs(inv.invoiceDate).format("MMM YYYY");
      map[month] = (map[month] || 0) + (inv.totalAmount || 0);
    });

    return Object.entries(map).map(([month, total]) => ({
      month,
      total: +total.toFixed(2),
    }));
  }, [invoices]);

  // ================= PAYMENT MODE =================
  const paymentStats = useMemo(() => {
    const map = {};
    invoices.forEach((inv) => {
      const paymentMode = inv.paymentMode || "Unknown";
      map[paymentMode] = (map[paymentMode] || 0) + inv.totalAmount;
    });

    return Object.entries(map)
      .filter(([name]) => name && name !== "undefined" && name !== "null")
      .map(([name, value]) => ({
        name,
        value: +value.toFixed(2),
      }));
  }, [invoices]);

  // ================= CATEGORY SALES =================
  const categorySales = useMemo(() => {
    const map = {};
    invoices.forEach((inv) =>
      inv.cartItems?.forEach((item) => {
        map[item.category] = (map[item.category] || 0) + item.totalPrice;
      })
    );

    return Object.entries(map).map(([name, value]) => ({
      name,
      value: +value.toFixed(2),
    }));
  }, [invoices]);

  // Selected date for "Top Selling Products" (null = all time for non-pharmacy, dayjs for pharmacy default today)
  const [topProductsDate, setTopProductsDate] = useState(() =>
    isPharmacy ? dayjs() : null
  );

  // Report download: period and custom range
  const REPORT_PERIODS = { weekly: "weekly", monthly: "monthly", yearly: "yearly", custom: "custom" };
  const [reportPeriod, setReportPeriod] = useState(REPORT_PERIODS.weekly);
  const [customDateRange, setCustomDateRange] = useState(null);

  // Top products for the selected date (or all time when date is null)
  const topProductsForDate = useMemo(() => {
    const map = {};
    const dateStr = topProductsDate ? topProductsDate.format("YYYY-MM-DD") : null;

    invoices
      .filter((inv) => {
        if (!dateStr) return true;
        const invoiceDate = inv.invoiceDate || inv.createdAt;
        return dayjs(invoiceDate).format("YYYY-MM-DD") === dateStr;
      })
      .forEach((inv) =>
        inv.cartItems?.forEach((item) => {
          map[item.title] = (map[item.title] || 0) + item.quantity;
        })
      );

    return Object.entries(map)
      .map(([title, quantity]) => ({ title, quantity }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);
  }, [invoices, topProductsDate]);

  // ================= PEAK HOURS =================
  const peakHours = useMemo(() => {
    const map = {};
    invoices.forEach((inv) => {
      const hour = dayjs(inv.invoiceDate).format("HH");
      map[hour] = (map[hour] || 0) + inv.totalAmount;
    });

    return Object.entries(map).map(([hour, total]) => ({
      hour: `${hour}:00`,
      total: +total.toFixed(2),
    }));
  }, [invoices]);

  // ================= REPORT DOWNLOAD (CSV) =================
  const getReportDateRange = () => {
    const now = dayjs().endOf("day");
    if (reportPeriod === REPORT_PERIODS.weekly) {
      return { start: now.subtract(6, "day").startOf("day"), end: now };
    }
    if (reportPeriod === REPORT_PERIODS.monthly) {
      return { start: now.subtract(29, "day").startOf("day"), end: now };
    }
    if (reportPeriod === REPORT_PERIODS.yearly) {
      return { start: now.subtract(364, "day").startOf("day"), end: now };
    }
    if (reportPeriod === REPORT_PERIODS.custom && customDateRange?.[0] && customDateRange?.[1]) {
      return {
        start: customDateRange[0].startOf("day"),
        end: customDateRange[1].endOf("day"),
      };
    }
    return null;
  };

  const reportInvoices = useMemo(() => {
    const range = getReportDateRange();
    if (!range) return [];
    const startTs = range.start.valueOf();
    const endTs = range.end.valueOf();
    return invoices.filter((inv) => {
      const ts = new Date(inv.invoiceDate || inv.createdAt).getTime();
      return ts >= startTs && ts <= endTs;
    });
  }, [invoices, reportPeriod, customDateRange]);

  // ================= OPENING / CLOSING BALANCE FOR SELECTED PERIOD (ALL BUSINESSES) =================
  const periodBalances = useMemo(() => {
    const range = getReportDateRange();
    if (!range || !Array.isArray(invoices) || invoices.length === 0) {
      return { openingBalance: 0, closingBalance: 0, periodNetSales: 0 };
    }

    const startTs = range.start.valueOf();
    const endTs = range.end.valueOf();

    const getInvoiceNet = (inv) => {
      const subTotal = Number(inv.subTotal);
      if (!Number.isNaN(subTotal) && subTotal !== 0) {
        return subTotal;
      }

      const totalAmount = Number(inv.totalAmount) || 0;
      const tax = Number(inv.tax) || 0;
      const discount = Number(inv.discount) || 0;

      return totalAmount - tax - discount;
    };

    let opening = 0;
    let periodNet = 0;

    invoices.forEach((inv) => {
      const ts = new Date(inv.invoiceDate || inv.createdAt).getTime();
      if (!ts) return;
      const net = getInvoiceNet(inv);

      if (ts < startTs) {
        opening += net;
      } else if (ts >= startTs && ts <= endTs) {
        periodNet += net;
      }
    });

    const closing = opening + periodNet;

    return {
      openingBalance: Number(opening.toFixed(2)),
      closingBalance: Number(closing.toFixed(2)),
      periodNetSales: Number(periodNet.toFixed(2)),
    };
  }, [invoices, reportPeriod, customDateRange]);

  const escapeCsvCell = (val) => {
    const str = String(val ?? "");
    if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const buildReportCsv = () => {
    const range = getReportDateRange();
    if (!range) return null;

    let totalSales = 0;
    let totalTax = 0;
    let totalDiscount = 0;
    let itemsSold = 0;
    reportInvoices.forEach((inv) => {
      totalSales += inv.totalAmount || 0;
      totalTax += inv.tax || 0;
      totalDiscount += inv.discount || 0;
      inv.cartItems?.forEach((i) => { itemsSold += i.quantity || 0; });
    });
    const netSales = totalSales - totalTax - totalDiscount;

    const periodLabel =
      reportPeriod === REPORT_PERIODS.weekly
        ? t("reportWeeklyLabel")
        : reportPeriod === REPORT_PERIODS.monthly
          ? t("reportMonthlyLabel")
          : reportPeriod === REPORT_PERIODS.yearly
            ? t("reportYearlyLabel")
            : t("reportCustomLabel", {
              from: range.start.format("MMM DD, YYYY"),
              to: range.end.format("MMM DD, YYYY"),
            });

    const rows = [];
    rows.push([t("posStatistics"), periodLabel]);
    rows.push([]);
    rows.push([t("reportSummary"), ""]);
    rows.push([t("totalSales"), totalSales.toFixed(2)]);
    rows.push([t("netSales"), netSales.toFixed(2)]);
    rows.push([t("taxCollected"), totalTax.toFixed(2)]);
    rows.push([t("discount"), totalDiscount.toFixed(2)]);
    rows.push([t("invoices"), reportInvoices.length]);
    rows.push([t("reportItemsSold"), itemsSold]);
    rows.push([]);

    const daysDiff = range.end.diff(range.start, "day") + 1;
    const useDailyBreakdown = daysDiff <= 31;
    if (useDailyBreakdown) {
      rows.push([t("reportDate"), t("totalSales"), t("invoices")]);
      for (let d = 0; d < daysDiff; d++) {
        const date = range.start.add(d, "day");
        const dateStr = date.format("YYYY-MM-DD");
        const dayInvs = reportInvoices.filter(
          (inv) => dayjs(inv.invoiceDate || inv.createdAt).format("YYYY-MM-DD") === dateStr
        );
        const dayTotal = dayInvs.reduce((s, inv) => s + (inv.totalAmount || 0), 0);
        rows.push([dateStr, dayTotal.toFixed(2), dayInvs.length]);
      }
    } else {
      rows.push([t("month"), t("totalSales"), t("invoices")]);
      const monthMap = {};
      reportInvoices.forEach((inv) => {
        const monthKey = dayjs(inv.invoiceDate || inv.createdAt).format("YYYY-MM");
        if (!monthMap[monthKey]) monthMap[monthKey] = { total: 0, count: 0 };
        monthMap[monthKey].total += inv.totalAmount || 0;
        monthMap[monthKey].count += 1;
      });
      Object.entries(monthMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([month, { total, count }]) => {
          rows.push([month, total.toFixed(2), count]);
        });
    }

    return rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
  };

  const handleDownloadReport = () => {
    if (reportPeriod === REPORT_PERIODS.custom && (!customDateRange?.[0] || !customDateRange?.[1])) {
      message.warning(t("selectFromAndToDates"));
      return;
    }
    if (customDateRange?.[0] && customDateRange?.[1] && customDateRange[1].isBefore(customDateRange[0])) {
      message.warning(t("toDateBeforeFrom"));
      return;
    }
    const csv = buildReportCsv();
    if (!csv) {
      message.warning(t("selectReportPeriod"));
      return;
    }
    const range = getReportDateRange();
    const filename =
      range &&
      `pos-report-${range.start.format("YYYY-MM-DD")}-to-${range.end.format("YYYY-MM-DD")}.csv`;
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "pos-report.csv";
    a.click();
    URL.revokeObjectURL(url);
    message.success(t("reportDownloaded"));
  };

  // ================= PHARMACY POS INVOICES FILTER (for monthly ledger only) =================
  const pharmacyInvoices = useMemo(() => {
    if (!isPharmacy) return [];
    return invoices.filter((inv) => inv.invoiceType === INVOICE_TYPE_POS);
  }, [invoices, isPharmacy]);

  // ================= LEDGER INVOICES (all types: POS + Digital, for daily ledger) =================
  const ledgerInvoices = useMemo(() => invoices, [invoices]);

  // ================= MONTHLY SALES SUMMARY LEDGER =================
  const monthlySalesSummary = useMemo(() => {
    if (!isPharmacy || pharmacyInvoices.length === 0) return [];

    const monthlyMap = {};

    pharmacyInvoices.forEach((inv) => {
      // Use invoiceDate if available, otherwise createdAt
      const invoiceDate = inv.invoiceDate || inv.createdAt;
      const monthKey = dayjs(invoiceDate).format("YYYY-MM");
      const monthLabel = dayjs(invoiceDate).format("MMM YYYY");

      if (!monthlyMap[monthKey]) {
        monthlyMap[monthKey] = {
          key: monthKey,
          month: monthLabel,
          totalTaxableSales: 0,
          totalExemptSales: 0,
          salesTaxCollected: 0,
          netSales: 0,
          totalInvoices: 0,
        };
      }

      monthlyMap[monthKey].totalInvoices += 1;

      // Process each cart item to separate taxable and exempt
      inv.cartItems?.forEach((item) => {
        const quantity = Number(item.quantity) || 0;
        const unitPrice = Number(item.price) || 0;
        const itemValue = quantity * unitPrice; // Value without tax
        const taxRate = Number(item.taxRate) || 0;
        const itemTax = Number(item.tax) || 0;

        if (taxRate > 0) {
          // Taxable item (18% typically)
          monthlyMap[monthKey].totalTaxableSales += itemValue;
          monthlyMap[monthKey].salesTaxCollected += itemTax;
        } else {
          // Exempt or zero-rated item
          monthlyMap[monthKey].totalExemptSales += itemValue;
        }
      });

      // Net sales = subtotal (sales value before tax, after discounts)
      // subTotal already accounts for product-level discounts
      monthlyMap[monthKey].netSales += Number(inv.subTotal) || 0;
    });

    // Convert to array (oldest first) to compute running balances
    const itemsAsc = Object.values(monthlyMap).sort((a, b) => a.key.localeCompare(b.key));

    let runningBalance = 0;

    const withBalancesAsc = itemsAsc.map((item) => {
      const openingBalance = runningBalance;
      runningBalance += item.netSales;
      const closingBalance = runningBalance;

      return {
        ...item,
        totalTaxableSales: Number(item.totalTaxableSales.toFixed(2)),
        totalExemptSales: Number(item.totalExemptSales.toFixed(2)),
        salesTaxCollected: Number(item.salesTaxCollected.toFixed(2)),
        netSales: Number(item.netSales.toFixed(2)),
        openingBalance: Number(openingBalance.toFixed(2)),
        closingBalance: Number(closingBalance.toFixed(2)),
      };
    });

    // Show newest month first in UI
    return withBalancesAsc.sort((a, b) => b.key.localeCompare(a.key));
  }, [pharmacyInvoices, isPharmacy]);

  // ================= DAILY SALES SUMMARY LEDGER (opening/closing per day) – all users, all invoice types =================
  const dailySalesSummary = useMemo(() => {
    if (!Array.isArray(ledgerInvoices) || ledgerInvoices.length === 0) return [];

    const dailyMap = {};

    ledgerInvoices.forEach((inv) => {
      const invoiceDate = inv.invoiceDate || inv.createdAt;
      const dateKey = dayjs(invoiceDate).format("YYYY-MM-DD");
      const dateLabel = dayjs(invoiceDate).format("MMM DD, YYYY");

      if (!dailyMap[dateKey]) {
        dailyMap[dateKey] = {
          key: dateKey,
          date: dateLabel,
          totalTaxableSales: 0,
          totalExemptSales: 0,
          salesTaxCollected: 0,
          netSales: 0,
          totalInvoices: 0,
        };
      }

      dailyMap[dateKey].totalInvoices += 1;

      inv.cartItems?.forEach((item) => {
        const quantity = Number(item.quantity) || 0;
        const unitPrice = Number(item.price) || 0;
        const itemValue = quantity * unitPrice;
        const taxRate = Number(item.taxRate) || 0;
        const itemTax = Number(item.tax) || 0;

        if (taxRate > 0) {
          dailyMap[dateKey].totalTaxableSales += itemValue;
          dailyMap[dateKey].salesTaxCollected += itemTax;
        } else {
          dailyMap[dateKey].totalExemptSales += itemValue;
        }
      });

      dailyMap[dateKey].netSales += Number(inv.subTotal) || 0;
    });

    const itemsAsc = Object.values(dailyMap).sort((a, b) => a.key.localeCompare(b.key));
    let prevClosing = 0;

    const withBalancesAsc = itemsAsc.map((item) => {
      const override = openingBalanceOverrides[item.key];
      const openingBalance = override !== undefined && override !== null
        ? Number(override)
        : prevClosing;
      const closingBalance = openingBalance + item.netSales;
      prevClosing = closingBalance;
      return {
        ...item,
        totalTaxableSales: Number(item.totalTaxableSales.toFixed(2)),
        totalExemptSales: Number(item.totalExemptSales.toFixed(2)),
        salesTaxCollected: Number(item.salesTaxCollected.toFixed(2)),
        netSales: Number(item.netSales.toFixed(2)),
        openingBalance: Number(openingBalance.toFixed(2)),
        closingBalance: Number(closingBalance.toFixed(2)),
      };
    });

    // Filter to selected date range
    if (dailyLedgerDateRange?.[0] && dailyLedgerDateRange?.[1]) {
      const start = dailyLedgerDateRange[0].format("YYYY-MM-DD");
      const end = dailyLedgerDateRange[1].format("YYYY-MM-DD");
      const filtered = withBalancesAsc.filter((item) => item.key >= start && item.key <= end);
      return filtered.sort((a, b) => b.key.localeCompare(a.key)); // newest first
    }

    return withBalancesAsc.sort((a, b) => b.key.localeCompare(a.key));
  }, [ledgerInvoices, dailyLedgerDateRange, openingBalanceOverrides]);

  // ================= EDITED INVOICES (ALL TYPES) =================
  const editedInvoices = useMemo(() => {
    if (!Array.isArray(invoices) || invoices.length === 0) return [];

    const items = invoices.filter((inv) => {
      if (!inv.updatedAt && !inv.lastEditedAt) return false;
      const updated = inv.lastEditedAt || inv.updatedAt;
      const created = inv.createdAt;
      if (!updated || !created) return false;
      return new Date(updated).getTime() !== new Date(created).getTime();
    });

    return items
      .sort((a, b) => {
        const aTime = new Date(a.lastEditedAt || a.updatedAt || 0).getTime();
        const bTime = new Date(b.lastEditedAt || b.updatedAt || 0).getTime();
        return bTime - aTime;
      })
      .slice(0, 15);
  }, [invoices]);

  const handleCopyInvoiceNumber = async (invoiceNumber) => {
    if (!invoiceNumber || invoiceNumber === "NA") {
      message.warning(t("noInvoiceNumberToCopy"));
      return;
    }

    try {
      await navigator.clipboard.writeText(invoiceNumber);
      message.success(t("invoiceNumberCopied", { invoiceNumber }));
    } catch (error) {
      console.error("Failed to copy:", error);
      message.error(t("failedToCopyInvoiceNumber"));
    }
  };

  const handleSaveOpeningBalance = async (dateKey, value) => {
    if (!canEditOpeningBalance) {
      message.warning(t("selectBranchToEditOpening"));
      return;
    }
    const num = Number(value);
    if (Number.isNaN(num) || num < 0) {
      message.warning(t("openingBalanceMustBeNonNegative"));
      return;
    }
    setEditingOpeningDate(null);
    try {
      const body = {
        date: dateKey,
        openingBalance: Math.round(num * 100) / 100,
      };
      if (effectiveBranchIdForLedger) body.branchId = effectiveBranchIdForLedger;
      await apiRequest("/api/daily-ledger/overrides", {
        method: "PUT",
        body: JSON.stringify(body),
      });
      setOpeningBalanceOverrides((prev) => ({ ...prev, [dateKey]: num }));
      message.success(t("openingBalanceSaved"));
    } catch (err) {
      message.error(err?.message || t("failedToSaveOpeningBalance"));
      setEditingOpeningDate(dateKey);
    }
  };

  const editedInvoiceColumns = [
    {
      title: t("invoiceNo"),
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 130,
      render: (value, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span>{value || t("na")}</span>
          {value && value !== "NA" && (
            <CopyOutlined
              onClick={() => handleCopyInvoiceNumber(value)}
              style={{
                cursor: "pointer",
                color: "#1890ff",
                fontSize: "14px",
              }}
              title={t("copyInvoiceNumber")}
            />
          )}
          {record.invoiceType === INVOICE_TYPE_POS && (
            <Tag color="blue" style={{ marginLeft: 4 }}>
              {t("posTag")}
            </Tag>
          )}
          {record.invoiceType === INVOICE_TYPE_DIGITAL_INVOICING && (
            <Tag color="purple" style={{ marginLeft: 4 }}>
              {t("digitalTag")}
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: t("customer"),
      dataIndex: "customerName",
      key: "customerName",
      width: 180,
      render: (value) => value || t("na"),
    },
    {
      title: t("editedOn"),
      dataIndex: "lastEditedAt",
      key: "lastEditedAt",
      width: 200,
      render: (value, record) => {
        const ts = value || record.updatedAt;
        if (!ts) return t("na");
        return new Date(ts).toLocaleString("en-PK");
      },
    },
    {
      title: t("originalCreated"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      render: (value) => (value ? new Date(value).toLocaleString("en-PK") : t("na")),
    },
    {
      title: t("editReason"),
      dataIndex: "lastEditReason",
      key: "lastEditReason",
      width: 220,
      ellipsis: true,
      render: (value) => (value ? value : t("noEditReasonDisplay")),
    },
    {
      title: t("payment"),
      dataIndex: "paymentMode",
      key: "paymentMode",
      width: 140,
      render: (value) => value || t("na"),
    },
    {
      title: t("totalAmount"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 140,
      render: (value) =>
        typeof value === "number"
          ? `Rs. ${value.toLocaleString("en-PK", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
          : t("na"),
    },
  ];

  // ================= MONTHLY SALES SUMMARY TABLE COLUMNS =================
  const monthlySummaryColumns = [
    {
      title: t("month"),
      dataIndex: "month",
      key: "month",
      fixed: "left",
      width: 120,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: t("totalInvoices"),
      dataIndex: "totalInvoices",
      key: "totalInvoices",
      align: "center",
      width: 120,
    },
    {
      title: t("taxableSales"),
      dataIndex: "totalTaxableSales",
      key: "totalTaxableSales",
      align: "right",
      width: 140,
      render: (value) => `Rs. ${value.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      title: t("exemptSales"),
      dataIndex: "totalExemptSales",
      key: "totalExemptSales",
      align: "right",
      width: 180,
      render: (value) => `Rs. ${value.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      title: t("salesTaxCollected"),
      dataIndex: "salesTaxCollected",
      key: "salesTaxCollected",
      align: "right",
      width: 160,
      render: (value) => (
        <Tag color="green" style={{ fontSize: "13px", padding: "4px 8px" }}>
          Rs. {value.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Tag>
      ),
    },
    {
      title: t("netSales"),
      dataIndex: "netSales",
      key: "netSales",
      align: "right",
      width: 140,
      render: (value) => (
        <strong style={{ color: "#1890ff" }}>
          Rs. {value.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </strong>
      ),
    },
    {
      title: t("openingBalance"),
      dataIndex: "openingBalance",
      key: "openingBalance",
      align: "right",
      width: 160,
      render: (value) =>
        `Rs. ${value.toLocaleString("en-PK", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      title: t("closingBalance"),
      dataIndex: "closingBalance",
      key: "closingBalance",
      align: "right",
      width: 160,
      render: (value) => (
        <strong>
          Rs. {value.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </strong>
      ),
    },
  ];

  // ================= DAILY SALES SUMMARY TABLE COLUMNS =================
  const dailySummaryColumns = [
    {
      title: t("reportDate"),
      dataIndex: "date",
      key: "date",
      fixed: "left",
      width: 140,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: t("totalInvoices"),
      dataIndex: "totalInvoices",
      key: "totalInvoices",
      align: "center",
      width: 120,
    },
    {
      title: t("taxableSales"),
      dataIndex: "totalTaxableSales",
      key: "totalTaxableSales",
      align: "right",
      width: 140,
      render: (value) => `Rs. ${value.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      title: t("exemptSales"),
      dataIndex: "totalExemptSales",
      key: "totalExemptSales",
      align: "right",
      width: 180,
      render: (value) => `Rs. ${value.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      title: t("salesTaxCollected"),
      dataIndex: "salesTaxCollected",
      key: "salesTaxCollected",
      align: "right",
      width: 160,
      render: (value) => (
        <Tag color="green" style={{ fontSize: "13px", padding: "4px 8px" }}>
          Rs. {value.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Tag>
      ),
    },
    {
      title: t("netSales"),
      dataIndex: "netSales",
      key: "netSales",
      align: "right",
      width: 140,
      render: (value) => (
        <strong style={{ color: "#1890ff" }}>
          Rs. {value.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </strong>
      ),
    },
    {
      title: t("openingBalance"),
      dataIndex: "openingBalance",
      key: "openingBalance",
      align: "right",
      width: 180,
      render: (value, record) => {
        const isEditing = editingOpeningDate === record.key;
        const canEdit = canEditOpeningBalance;
        if (isEditing && canEdit) {
          const saveValue = () => {
            const v = editingOpeningValueRef.current ?? value;
            handleSaveOpeningBalance(record.key, v);
          };
          return (
            <InputNumber
              autoFocus
              min={0}
              step={0.01}
              precision={2}
              defaultValue={value}
              onChange={(v) => { editingOpeningValueRef.current = v; }}
              onPressEnter={saveValue}
              onBlur={saveValue}
              className="w-full"
              style={{ width: "100%" }}
            />
          );
        }
        return (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              if (canEdit) {
                editingOpeningValueRef.current = value;
                setEditingOpeningDate(record.key);
              } else {
                message.warning(t("selectBranchToEditOpening"));
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                if (canEdit) {
                  editingOpeningValueRef.current = value;
                  setEditingOpeningDate(record.key);
                } else {
                  message.warning(t("selectBranchToEditOpening"));
                }
              }
            }}
            className={canEdit ? "cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 inline-flex items-center gap-1" : "inline-flex items-center gap-1"}
            title={canEdit ? t("clickToEditOpening") : t("selectBranchToEditOpening")}
          >
            Rs. {value.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {canEdit && <EditOutlined style={{ fontSize: 12, color: "#1890ff" }} />}
          </span>
        );
      },
    },
    {
      title: t("closingBalance"),
      dataIndex: "closingBalance",
      key: "closingBalance",
      align: "right",
      width: 160,
      render: (value) => (
        <strong>
          Rs. {value.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </strong>
      ),
    },
  ];

  // ================= UI =================
  return (
    <>
      <Header />
      <div className="px-6 pb-20">
        <h1 className="text-2xl text-center font-semibold mb-6 text-gray-800">{t("posStatistics")}</h1>

        {/* SUMMARY CARDS */}
        <div className="grid xl:grid-cols-4 md:grid-cols-2 gap-6 mb-10">
          <StatisticCard
            title={t("totalSales")}
            amount={stats.totalSales}
            icon={<DollarCircleOutlined className="text-emerald-600 text-xl" />}
          />
          <StatisticCard
            title={t("netSales")}
            amount={stats.netSales}
            icon={<LineChartOutlined className="text-blue-600 text-xl" />}
          />
          <StatisticCard
            title={t("taxCollected")}
            amount={stats.totalTax}
            icon={<PercentageOutlined className="text-purple-600 text-xl" />}
          />
          <StatisticCard
            title={t("invoices")}
            amount={stats.totalInvoices}
            icon={<FileTextOutlined className="text-orange-600 text-xl" />}
          />
          <StatisticCard
            title={t("openingBalance")}
            amount={periodBalances.openingBalance.toFixed(2)}
            icon={<WalletOutlined className="text-indigo-400 text-xl" />}
          />
          <StatisticCard
            title={t("closingBalance")}
            amount={periodBalances.closingBalance.toFixed(2)}
            icon={<BankOutlined className="text-emerald-500 text-xl" />}
          />
        </div>

        {/* DOWNLOAD REPORT (CSV) */}
        <Card className="mb-8" title={t("downloadReport")}>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t("reportPeriod")}</label>
              <Select
                value={reportPeriod}
                onChange={setReportPeriod}
                options={[
                  { value: REPORT_PERIODS.weekly, label: t("reportWeekly") },
                  { value: REPORT_PERIODS.monthly, label: t("reportMonthly") },
                  { value: REPORT_PERIODS.yearly, label: t("reportYearly") },
                  { value: REPORT_PERIODS.custom, label: t("reportCustom") },
                ]}
                style={{ minWidth: 160 }}
              />
            </div>
            {reportPeriod === REPORT_PERIODS.custom && (
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t("reportDateRange")}</label>
                <DatePicker.RangePicker
                  value={customDateRange}
                  onChange={(dates) => setCustomDateRange(dates?.length === 2 ? dates : null)}
                  format="MMM DD, YYYY"
                  maxDate={dayjs()}
                  className="w-[280px]"
                />
              </div>
            )}
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadReport}>
              {t("downloadCsv")}
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-3 mb-0">
            {t("downloadReportHint")}
          </p>
        </Card>

        {/* CHARTS */}
        <div className="grid xl:grid-cols-2 gap-8">
          <Card title={t("last7DaysSales")}>
            <Line
              data={last7DaysSales}
              xField="date"
              yField="total"
              height={300}
              color="#1677ff"
              point={{
                size: 5,
                shape: "circle",
              }}
              smooth
            />
          </Card>

          <Card title={t("monthlySales")}>
            <Column
              data={monthlySales}
              xField="month"
              yField="total"
              height={300}
              color="#1677ff"
            />
          </Card>

          <Card title={t("paymentModes")}>
            <Pie
              data={paymentStats}
              angleField="value"
              colorField="name"
              height={300}
              radius={0.8}
              color={(datum) => {
                const index = paymentStats.findIndex((item) => item.name === datum.name);
                return CHART_COLORS[index % CHART_COLORS.length];
              }}
              label={false}
              statistic={false}
              legend={{
                position: "bottom",
              }}
            />
          </Card>

          {/* Category Sales - Only show for non-pharmacy (restaurant/hotel) */}
          {!isPharmacy && (
            <Card title={t("categorySales")}>
              <Pie
                data={categorySales}
                angleField="value"
                colorField="name"
                height={300}
                radius={0.8}
                color={(datum) => {
                  const index = categorySales.findIndex((item) => item.name === datum.name);
                  return CHART_COLORS[index % CHART_COLORS.length];
                }}
                label={false}
                statistic={false}
                legend={{
                  position: "bottom",
                }}
              />
            </Card>
          )}
        </div>

        {/* TOP PRODUCTS */}
        <Card
          title={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{t("topSellingProducts")}</span>
              <div className="flex items-center gap-2">
                <DatePicker
                  value={topProductsDate}
                  onChange={(date) => setTopProductsDate(date)}
                  format="MMM DD, YYYY"
                  allowClear={!isPharmacy}
                  placeholder={isPharmacy ? undefined : t("allTime")}
                  maxDate={dayjs()}
                  className="w-[160px]"
                />
                {topProductsDate && (
                  <Tag color="blue" style={{ fontSize: "12px" }}>
                    {topProductsDate.format("MMM DD, YYYY")}
                  </Tag>
                )}
              </div>
            </div>
          }
          className="mt-10"
        >
          {topProductsForDate.length > 0 ? (
            topProductsForDate.map((p, i) => (
              <div key={i} className="flex justify-between border-b py-2">
                <span>{i + 1}. {p.title}</span>
                <span>{p.quantity}</span>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>
                {t("noProductsSold")}
                {topProductsDate
                  ? t("noProductsSoldOnDate", { date: topProductsDate.format("MMM DD, YYYY") })
                  : t("noProductsSoldYet")}
              </p>
            </div>
          )}
        </Card>

        {/* DAILY SALES SUMMARY LEDGER – opening/closing balance per day (all users, all invoice types) */}
        <Card
          title={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-lg font-semibold text-gray-800">{t("dailySalesSummaryLedger")}</span>
              <Tag color="blue" style={{ fontSize: "12px" }}>
                {t("dailyLedgerAllSales")}
              </Tag>
            </div>
          }
          className="mt-10"
        >
            <div className="mb-4 text-sm text-gray-600">
              <p>{t("dailySalesSummaryLedgerDesc")}</p>
            </div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <label className="text-sm font-medium text-gray-700">{t("reportDateRange")}</label>
              <DatePicker.RangePicker
                value={dailyLedgerDateRange}
                onChange={(dates) => setDailyLedgerDateRange(dates?.length === 2 ? dates : null)}
                format="MMM DD, YYYY"
                maxDate={dayjs()}
                className="w-[280px]"
              />
              {!effectiveBranchIdForLedger && ledgerBranches.length > 1 && (
                <Tag color="orange">{t("selectBranchToEditOpening")}</Tag>
              )}
            </div>
            {dailySalesSummary.length > 0 ? (
              <Table
                dataSource={dailySalesSummary}
                columns={dailySummaryColumns}
                rowKey="key"
                pagination={{
                  pageSize: 8,
                  showSizeChanger: true,
                  showTotal: (total) => t("totalDays", { total }),
                }}
                scroll={{ x: 800, y: 320 }}
                bordered
                size="middle"
                summary={(pageData) => {
                  let totalTaxable = 0;
                  let totalExempt = 0;
                  let totalTax = 0;
                  let totalNet = 0;
                  let totalInvoices = 0;
                  let pageOpeningMin = null;
                  let pageClosingMax = null;

                  pageData.forEach(
                    ({
                      totalTaxableSales,
                      totalExemptSales,
                      salesTaxCollected,
                      netSales,
                      totalInvoices: invCount,
                      openingBalance,
                      closingBalance,
                    }) => {
                      totalTaxable += totalTaxableSales;
                      totalExempt += totalExemptSales;
                      totalTax += salesTaxCollected;
                      totalNet += netSales;
                      totalInvoices += invCount;

                      if (typeof openingBalance === "number") {
                        if (pageOpeningMin === null || openingBalance < pageOpeningMin) {
                          pageOpeningMin = openingBalance;
                        }
                      }

                      if (typeof closingBalance === "number") {
                        if (pageClosingMax === null || closingBalance > pageClosingMax) {
                          pageClosingMax = closingBalance;
                        }
                      }
                    }
                  );

                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ backgroundColor: "#f0f9ff" }}>
                        <Table.Summary.Cell index={0}>
                          <strong>{t("grandTotal")}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="center">
                          <strong>{totalInvoices}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                          <strong>
                            Rs. {totalTaxable.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <strong>
                            Rs. {totalExempt.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <Tag color="green" style={{ fontSize: "13px", padding: "4px 8px" }}>
                            <strong>
                              Rs. {totalTax.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </strong>
                          </Tag>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          <strong style={{ color: "#1890ff", fontSize: "15px" }}>
                            Rs. {totalNet.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">
                          {pageOpeningMin !== null && (
                            <strong>
                              Rs.{" "}
                              {pageOpeningMin.toLocaleString("en-PK", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </strong>
                          )}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7} align="right">
                          {pageClosingMax !== null && (
                            <strong>
                              Rs.{" "}
                              {pageClosingMax.toLocaleString("en-PK", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </strong>
                          )}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>{t("noLedgerInvoices")}</p>
                <p className="text-sm mt-2">{t("dailyLedgerDateRangeHint")}</p>
              </div>
            )}
        </Card>

        {/* RECENTLY EDITED INVOICES (All businesses – pharmacy & restaurant) */}
        <Card
          title={
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-gray-800">
                {t("recentlyEditedInvoices")}
              </span>
              {editedInvoices.length > 0 && (
                <Tag color="gold" style={{ fontSize: "12px" }}>
                  {t("showingLastEdits", { count: editedInvoices.length })}
                </Tag>
              )}
            </div>
          }
          className="mt-10"
        >
          <div className="mb-3 text-sm text-gray-600">
            {t("recentlyEditedInvoicesDesc")}
          </div>
          {editedInvoices.length > 0 ? (
            <Table
              rowKey="_id"
              dataSource={editedInvoices}
              columns={editedInvoiceColumns}
              size="small"
              pagination={false}
              scroll={{
                x: 1120,
                ...(editedInvoices.length > 5 && { y: 280 }),
              }}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              {t("noEditedInvoices")}
            </div>
          )}
        </Card>
      </div>
    </>
  );
};

export default StatisticPage;
